import { motion } from 'framer-motion'
import { Construction, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader, type BreadcrumbItem, fadeUp, staggerContainer, staggerItem } from '@personic/shared'

interface ScaffoldPageProps {
  title: string
  description: string
  breadcrumbs: BreadcrumbItem[]
  plannedFeatures?: string[]
  relatedPages?: Array<{ label: string; href: string }>
}

export function ScaffoldPage({ title, description, breadcrumbs, plannedFeatures, relatedPages }: ScaffoldPageProps) {
  return (
    <div>
      <PageHeader title={title} description={description} breadcrumbs={breadcrumbs} />

      <motion.div variants={fadeUp} initial="hidden" animate="visible" className="max-w-2xl">
        <div className="rounded-xl border border-warm-200 bg-white p-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-copper-50 flex items-center justify-center">
              <Construction className="w-5 h-5 text-copper-500" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-midnight-800">Under Development</h3>
              <p className="text-sm text-warm-500">This page is being built as part of the platform redesign.</p>
            </div>
          </div>

          {plannedFeatures && plannedFeatures.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-warm-700 mb-3">Planned capabilities:</h4>
              <motion.ul variants={staggerContainer} initial="hidden" animate="visible" className="space-y-2">
                {plannedFeatures.map((feature, i) => (
                  <motion.li key={i} variants={staggerItem} className="flex items-start gap-2 text-sm text-warm-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-copper-400 mt-1.5 flex-shrink-0" />
                    {feature}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          )}

          {relatedPages && relatedPages.length > 0 && (
            <div className="mt-6 pt-6 border-t border-warm-100">
              <h4 className="text-sm font-medium text-warm-700 mb-3">Related pages:</h4>
              <div className="space-y-2">
                {relatedPages.map((page, i) => (
                  <Link
                    key={i}
                    to={page.href}
                    className="flex items-center gap-2 text-sm text-copper-600 hover:text-copper-700 transition-colors"
                  >
                    <ArrowRight className="w-3.5 h-3.5" />
                    {page.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
